"use client"

import { useState, useEffect, useContext } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Card,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import BusinessIcon from "@mui/icons-material/Business"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import UndoIcon from "@mui/icons-material/Undo"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import Chip from "@mui/material/Chip"
import Tooltip from "@mui/material/Tooltip"
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices"
import PaymentIcon from "@mui/icons-material/Payment"
import AccountBalanceIcon from "@mui/icons-material/AccountBalance"

import { generarPdfProveedorDetalleCC, generarPdfReciboTransaccionProveedor } from "../../context/pdf_esqueletos/proveedorDetalleCuentaCorrientePdf"
import { reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js"
import TransaccionModal from "../transaccionComponent/TransaccionModal.js"

const ModalDetalleProveedorCC = ({ open, handleClose, idEntidad }) => {
  const theme = useTheme()
  const { user, getDetalleProveedorCuentaCorriente, revertirTransaccionProveedor, getParametrosGlobales } = useContext(ErpContext)

  const [loading, setLoading] = useState(false)
  const [entidad, setEntidad] = useState(null)
  const [cuenta, setCuenta] = useState({ montoAcumulado: 0, cobradoAcumulado: 0, saldoDinamico: 0 })
  const [transacciones, setTransacciones] = useState([])

  const [filtroServicio, setFiltroServicio] = useState(true)
  const [filtroPago, setFiltroPago] = useState(true)
  const [filtroCtaCte, setFiltroCtaCte] = useState(true)

  const currentYear = new Date().getFullYear()
  const [fechaDesde, setFechaDesde] = useState(`${currentYear}-01-01`)
  const [fechaHasta, setFechaHasta] = useState(`${currentYear}-12-31`)

  // Estado del popup de reversión
  const [revertirDialog, setRevertirDialog] = useState({ open: false, tx: null })
  const [opcionReversion, setOpcionReversion] = useState("cuentaCorriente")
  const [loadingReversion, setLoadingReversion] = useState(false)
  const [mensajeReversion, setMensajeReversion] = useState(null)

  // Modal para registrar ingreso cuando se selecciona "Devuelve Pago"
  const [transaccionModalDevolucion, setTransaccionModalDevolucion] = useState(false)
  const [idTransaccionParaDevolucion, setIdTransaccionParaDevolucion] = useState(null)
  const [montoDevolucionSugerido, setMontoDevolucionSugerido] = useState(null)

  const fetchData = async () => {
    if (!idEntidad) return
    setLoading(true)
    try {
      const data = await getDetalleProveedorCuentaCorriente(idEntidad)
      setEntidad(data.entidad)
      setCuenta(data.cuenta)
      setTransacciones(data.transacciones || [])
    } catch (err) {
      console.error("Error cargando detalle proveedor CC:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open || !idEntidad) return
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idEntidad])

  useEffect(() => {
    if (!open) {
      setEntidad(null)
      setCuenta({ montoAcumulado: 0, cobradoAcumulado: 0, saldoDinamico: 0 })
      setTransacciones([])
      setFiltroServicio(true)
      setFiltroPago(true)
      setFiltroCtaCte(true)
      const y = new Date().getFullYear()
      setFechaDesde(`${y}-01-01`)
      setFechaHasta(`${y}-12-31`)
      setRevertirDialog({ open: false, tx: null })
      setMensajeReversion(null)
      setTransaccionModalDevolucion(false)
      setIdTransaccionParaDevolucion(null)
      setMontoDevolucionSugerido(null)
    }
  }, [open])

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value || 0)

  const parseDateSafe = (value) => {
    if (!value) return null
    const d = new Date(value)
    if (!isNaN(d.getTime())) return d
    return null
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const dateObj = parseDateSafe(dateString)
    if (!dateObj) return String(dateString)
    return new Intl.DateTimeFormat("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(dateObj)
  }

  const handleLimpiarFiltros = () => {
    const y = new Date().getFullYear()
    setFechaDesde(`${y}-01-01`)
    setFechaHasta(`${y}-12-31`)
    setFiltroServicio(true)
    setFiltroPago(true)
    setFiltroCtaCte(true)
  }

  const clasificarTransaccion = (tx) => {
    if (tx.tipo === "Servicio") {
      return {
        label: tx.esDevolucion ? "Reversión Servicio" : "Servicio",
        color: tx.esDevolucion ? "default" : "info",
        icono: <MiscellaneousServicesIcon sx={{ fontSize: 16 }} />,
        colorMonto: tx.esDevolucion ? theme.palette.text.secondary : theme.palette.info.main,
        bgMonto: tx.esDevolucion ? alpha(theme.palette.text.secondary, 0.08) : alpha(theme.palette.info.main, 0.1),
        tooltip: tx.esDevolucion ? "Reversión de servicio consumido" : "Servicio consumido del proveedor",
      }
    }
    if (tx.tipo === "Cta Cte") {
      return {
        label: tx.esDevolucion ? "Reversión a Crédito" : "Cta Cte",
        color: tx.esDevolucion ? "default" : "error",
        icono: <AccountBalanceIcon sx={{ fontSize: 16 }} />,
        colorMonto: tx.esDevolucion ? theme.palette.text.secondary : theme.palette.error.main,
        bgMonto: tx.esDevolucion ? alpha(theme.palette.text.secondary, 0.08) : alpha(theme.palette.error.main, 0.1),
        tooltip: tx.esDevolucion ? "Reversión de pago devuelta como crédito (monto acumulado no cambia)" : "Operación de cuenta corriente",
      }
    }
    return {
      label: tx.esDevolucion ? "Reversión Pago" : "Pago",
      color: tx.esDevolucion ? "default" : "success",
      icono: <PaymentIcon sx={{ fontSize: 16 }} />,
      colorMonto: tx.esDevolucion ? theme.palette.text.secondary : theme.palette.success.main,
      bgMonto: tx.esDevolucion ? alpha(theme.palette.text.secondary, 0.08) : alpha(theme.palette.success.main, 0.1),
      tooltip: tx.esDevolucion ? "Reversión de pago" : "Pago realizado al proveedor",
    }
  }

  const transaccionesFiltradas = transacciones.filter((tx) => {
    if (tx.tipo === "Servicio" && !filtroServicio) return false
    if (tx.tipo === "Pago" && !filtroPago) return false
    if (tx.tipo === "Cta Cte" && !filtroCtaCte) return false
    if (fechaDesde) {
      const d = parseDateSafe(tx.fecha)
      if (d && d < new Date(fechaDesde + "T00:00:00")) return false
    }
    if (fechaHasta) {
      const d = parseDateSafe(tx.fecha)
      if (d && d > new Date(fechaHasta + "T23:59:59")) return false
    }
    return true
  })

  const exportarPDF = async () => {
    const deliveryContext = { targetWindow: reservePdfTarget() }
    let logoBase64 = null
    try {
      const params = await getParametrosGlobales(["logoNavbarTenant"])
      logoBase64 = params?.logoNavbarTenant || null
    } catch (e) { /* ignorar */ }
    generarPdfProveedorDetalleCC({
      entidad,
      cuenta,
      transacciones: transaccionesFiltradas,
      fechaDesde,
      fechaHasta,
      usuario: user?.nombre || user?.usuario || "Usuario",
      logoBase64,
      deliveryContext,
    })
  }

  const exportarReciboPdf = async (tx) => {
    const deliveryContext = { targetWindow: reservePdfTarget() }
    let logoBase64 = null
    try {
      const params = await getParametrosGlobales(["logoNavbarTenant"])
      logoBase64 = params?.logoNavbarTenant || null
    } catch (e) { /* ignorar */ }
    generarPdfReciboTransaccionProveedor({
      entidad,
      tx,
      usuario: user?.nombre || user?.usuario || "Usuario",
      logoBase64,
      deliveryContext,
    })
  }

  const handleAbrirRevertir = (tx) => {
    setOpcionReversion("cuentaCorriente")
    setMensajeReversion(null)
    setRevertirDialog({ open: true, tx })
  }

  const handleConfirmarReversion = async () => {
    const { tx } = revertirDialog
    if (!tx) return
    setLoadingReversion(true)
    try {
      if (opcionReversion === "devolucion") {
        // NO reversar aún: abrir modal de Caja, la reversión se ejecuta cuando confirman el ingreso
        setRevertirDialog({ open: false, tx: null })
        setIdTransaccionParaDevolucion(tx.id)
        setMontoDevolucionSugerido(Math.abs(tx.monto || 0))
        setTransaccionModalDevolucion(true)
      } else {
        await revertirTransaccionProveedor(tx.id, opcionReversion)
        setRevertirDialog({ open: false, tx: null })
        if (opcionReversion === "perdida") {
          setMensajeReversion("Reversión registrada como pérdida.")
        }
        await fetchData()
      }
    } catch (err) {
      console.error("Error al revertir:", err)
      setMensajeReversion("Error al procesar la reversión.")
    } finally {
      setLoadingReversion(false)
    }
  }

  const columnas = ["ID", "Fecha", "Tipo", "Descripción", "Grupo", "Destino", "Monto", "Acciones"]

  if (!open) return null

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, bgcolor: theme.palette.primary.main, color: "white" }}>
          Detalle de Cuenta Corriente
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                          <BusinessIcon sx={{ mr: 1 }} /> Información del Proveedor
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">Nombre:</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {entidad ? `${entidad.descripcion || ""} ${entidad.apellido || ""}`.trim() : "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">DNI/CUIT/CUIL:</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {entidad?.dniCuitCuil || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2" color="text.secondary">Teléfono:</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {entidad?.telefono || "N/A"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Resumen de Cuenta</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary">Monto acumulado:</Typography>
                            <Typography variant="h6" fontWeight="bold" color={theme.palette.warning.main}>
                              {formatCurrency(cuenta.montoAcumulado)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary">Consumido:</Typography>
                            <Typography variant="h6" fontWeight="bold" color={theme.palette.success.main}>
                              {formatCurrency(cuenta.cobradoAcumulado)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary">Saldo dinámico:</Typography>
                            <Typography variant="h6" fontWeight="bold" color={theme.palette.primary.main}>
                              {formatCurrency(cuenta.saldoDinamico)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Filtros */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <TextField
                  type="date"
                  label="Desde"
                  size="small"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="date"
                  label="Hasta"
                  size="small"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchIcon />}
                  onClick={fetchData}
                  sx={{ minWidth: 100 }}
                >
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleLimpiarFiltros}
                  sx={{ minWidth: 120 }}
                >
                  Limpiar filtros
                </Button>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtroServicio}
                      onChange={() => setFiltroServicio((p) => !p)}
                      size="small"
                      sx={{ color: theme.palette.info.main, "&.Mui-checked": { color: theme.palette.info.main } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.85rem" }}>Servicio</Typography>}
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtroPago}
                      onChange={() => setFiltroPago((p) => !p)}
                      size="small"
                      sx={{ color: theme.palette.success.main, "&.Mui-checked": { color: theme.palette.success.main } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.85rem" }}>Pago</Typography>}
                  sx={{ m: 0 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtroCtaCte}
                      onChange={() => setFiltroCtaCte((p) => !p)}
                      size="small"
                      sx={{ color: theme.palette.error.main, "&.Mui-checked": { color: theme.palette.error.main } }}
                    />
                  }
                  label={<Typography sx={{ fontSize: "0.85rem" }}>Cta Cte</Typography>}
                  sx={{ m: 0 }}
                />
                <Box sx={{ ml: "auto" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={exportarPDF}
                    sx={{
                      color: "#e65100",
                      borderColor: "#e65100",
                      "&:hover": { borderColor: "#bf360c", backgroundColor: alpha("#e65100", 0.05) },
                    }}
                  >
                    Descargar PDF
                  </Button>
                </Box>
              </Box>

              {mensajeReversion && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, backgroundColor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                  <Typography variant="body2" color="info.main">{mensajeReversion}</Typography>
                </Box>
              )}

              <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {columnas.map((col) => (
                        <TableCell
                          key={col}
                          sx={{ backgroundColor: "#5b5b5f", color: "white", fontWeight: 600, fontSize: "0.78rem" }}
                        >
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transaccionesFiltradas.map((tx) => {
                      const clasificacion = clasificarTransaccion(tx)
                      const puedeRevertir = !tx.revertido && !tx.esDevolucion && (tx.tipo === "Pago" || tx.tipo === "Cta Cte")
                      return (
                        <TableRow
                          key={tx.id}
                          sx={{
                            opacity: tx.revertido || tx.esDevolucion ? 0.6 : 1,
                            "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                          }}
                        >
                          <TableCell sx={{ fontSize: "0.8rem" }}>{tx.id}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{formatDate(tx.fecha)}</TableCell>
                          <TableCell>
                            <Tooltip title={clasificacion.tooltip} arrow placement="top">
                              <Chip
                                icon={clasificacion.icono}
                                label={clasificacion.label}
                                color={clasificacion.color}
                                size="small"
                                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{tx.descripcion || "-"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{tx.grupo || "-"}</TableCell>
                          <TableCell sx={{ fontSize: "0.8rem" }}>{tx.destino || "-"}</TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: "inline-block",
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: clasificacion.bgMonto,
                                color: clasificacion.colorMonto,
                                fontWeight: 700,
                                textDecoration: tx.revertido ? "line-through" : "none",
                              }}
                            >
                              {formatCurrency(tx.monto || 0)}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", alignItems: "center" }}>
                              <Tooltip title="Recibo PDF - Explica qué sucedió en esta transacción">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<PictureAsPdfIcon />}
                                  onClick={() => exportarReciboPdf(tx)}
                                  sx={{
                                    minWidth: "auto",
                                    px: 1,
                                    py: 0.5,
                                    fontSize: "0.7rem",
                                    color: theme.palette.success.main,
                                    borderColor: theme.palette.success.main,
                                  }}
                                >
                                  PDF
                                </Button>
                              </Tooltip>
                              {puedeRevertir && (
                                <Tooltip title="Revertir">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleAbrirRevertir(tx)}
                                    sx={{
                                      color: theme.palette.error.main,
                                      "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.1) },
                                    }}
                                  >
                                    <UndoIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {tx.revertido && (
                                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, ml: 0.5 }}>
                                  Revertido
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    {transaccionesFiltradas.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={columnas.length} align="center" sx={{ py: 4, color: "#999" }}>
                          No hay transacciones disponibles
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Popup confirmación de reversión */}
      <Dialog open={revertirDialog.open} onClose={() => setRevertirDialog({ open: false, tx: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, bgcolor: theme.palette.error.main, color: "white" }}>
          Confirmar Reversión de {revertirDialog.tx?.tipo === "Pago" ? "Pago" : "Cuenta Corriente"}
          <IconButton
            onClick={() => setRevertirDialog({ open: false, tx: null })}
            sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ¿Está seguro que desea revertir el{" "}
            <strong>{revertirDialog.tx?.tipo}</strong> de{" "}
            <strong>{formatCurrency(Math.abs(revertirDialog.tx?.monto || 0))}</strong>?
          </Typography>

          {revertirDialog.tx?.tipo === "Pago" && (
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: "0.85rem", mb: 1 }}>Seleccione cómo se procesa la reversión:</FormLabel>
              <RadioGroup
                value={opcionReversion}
                onChange={(e) => setOpcionReversion(e.target.value)}
              >
                <FormControlLabel
                  value="cuentaCorriente"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>CUENTA CORRIENTE</Typography>
                      <Typography variant="caption" color="text.secondary">
                        El proveedor no devuelve y da crédito. Se acumula como saldo a favor.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  value="devolucion"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>DEVUELVE PAGO</Typography>
                      <Typography variant="caption" color="text.secondary">
                        El proveedor devuelve el dinero. Se abrirá el formulario para registrar el ingreso.
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  value="perdida"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>PÉRDIDA</Typography>
                      <Typography variant="caption" color="text.secondary">
                        El proveedor no devuelve ni da crédito. No se realiza ningún cambio financiero.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {revertirDialog.tx?.tipo === "Cta Cte" && (
            <Typography variant="body2" color="text.secondary">
              Esta reversión aumentará el <strong>Monto acumulado</strong> del proveedor.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setRevertirDialog({ open: false, tx: null })}
            disabled={loadingReversion}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={loadingReversion ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
            onClick={handleConfirmarReversion}
            disabled={loadingReversion}
          >
            Confirmar Reversión
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para registrar ingreso cuando el proveedor devuelve el pago */}
      <TransaccionModal
        open={transaccionModalDevolucion}
        onClose={() => {
          setTransaccionModalDevolucion(false)
          setIdTransaccionParaDevolucion(null)
          setMontoDevolucionSugerido(null)
        }}
        fechaTransaccion={new Date()}
        onTransactionAdded={fetchData}
        modoDevolucionProveedor={true}
        idEntidadProveedor={idEntidad}
        idTransaccionParaRevertir={idTransaccionParaDevolucion}
        montoSugerido={montoDevolucionSugerido}
        tituloPersonalizado="Registrar Ingreso - Devolución de Pago Proveedor"
      />
    </>
  )
}

export default ModalDetalleProveedorCC
